import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { sessionApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { formatDate } from '@/lib/format'
import { useSession } from '@/lib/session'

const PASSWORD_FIELDS = ['current_password', 'password', 'password_confirmation']

/**
 * `/{slug}/settings/account` — the signed-in person, not the workspace.
 *
 * Changing a password revokes every other browser session but deliberately
 * leaves bearer tokens alone, so an iPad mid-sync keeps its offline queue. The
 * copy says so rather than leaving people to discover it.
 */
export default function AccountPage() {
  const { user, signOut } = useSession()
  const navigate = useNavigate()

  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const change = useMutation({
    mutationFn: () =>
      sessionApi.changePassword({
        current_password: current,
        password,
        password_confirmation: confirmation,
      }),
    onSuccess: () => {
      setCurrent('')
      setPassword('')
      setConfirmation('')
    },
  })

  const resend = useMutation({ mutationFn: () => sessionApi.resendVerification() })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    change.mutate()
  }

  if (!user) return null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Your account" />
        <div className="flex flex-wrap items-center gap-4 p-5">
          <Avatar name={user.name} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className={cn('truncate text-sm', mutedTextClass)}>{user.email}</p>
            <p className={cn('mt-1 text-xs', mutedTextClass)}>
              Member since {formatDate(user.created_at)}
            </p>
          </div>
          {user.email_verified_at ? (
            <Badge tone="success">Email verified</Badge>
          ) : (
            <div className="flex flex-col items-end gap-1.5">
              <Badge tone="warning">Email unverified</Badge>
              <Button size="sm" loading={resend.isPending} onClick={() => resend.mutate()}>
                Resend link
              </Button>
            </div>
          )}
        </div>
        {resend.isSuccess ? (
          <div className="px-5 pb-5">
            <Alert tone="success">{resend.data.message}</Alert>
          </div>
        ) : null}
        {resend.error ? (
          <div className="px-5 pb-5">
            <Alert tone="danger">{messageFor(resend.error)}</Alert>
          </div>
        ) : null}
      </Card>

      <Card>
        <CardHeader
          title="Change password"
          description="Signs you out of every other browser. Your iPad and agent tokens keep working."
        />
        <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            error={fieldErrorFor(change.error, 'current_password')}
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            hint="At least 8 characters."
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={fieldErrorFor(change.error, 'password')}
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            error={fieldErrorFor(change.error, 'password_confirmation')}
          />

          {change.error && isFormLevel(change.error, PASSWORD_FIELDS) ? (
            <Alert tone="danger">{messageFor(change.error)}</Alert>
          ) : null}
          {change.isSuccess ? <Alert tone="success">{change.data.message}</Alert> : null}

          <div className="flex justify-end">
            <Button type="submit" variant="primary" loading={change.isPending}>
              Update password
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader title="Sessions" description="Sign out of this browser." />
        <div className="p-5">
          <Button
            variant="secondary"
            onClick={() => {
              void signOut().then(() => navigate('/login', { replace: true }))
            }}
          >
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  )
}
